import { useCallback, useEffect, useState } from "react";
import TaskHeader from "components/task/header/TaskHeader";
import TaskContent from "components/task/content/TaskContent";
import { Box, Stack } from "@mui/joy";
import { ProgramContentType } from "data/BlockComponent";
import * as _ from "lodash";
import useGetThoughtRecordData from "hooks/useGetThoughtRecordData";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import useGetCertainEventFromApp from "hooks/useGetCertainEventFromApp";
import { useSearchParams } from "react-router-dom";
import { theme } from "styles/theme";
import {
  THOUGHTRECORD_DOMAIN,
  THOUGHTRECORD_STATUS_DETAIL_ENDPOINT,
  thoughtRecordApi,
  ThoughtRecordStatusType,
} from "api/thoughtRecordApi";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { taskListAtom } from "recoil/taskListAtom";

export default function ThoughtRecordMobileDetailPage() {
  const [searchParams] = useSearchParams();
  const thoughtRecordKey = searchParams.get("trk");
  const taskListState = useRecoilValue(taskListAtom);

  const user = useRecoilValue(userAtom);

  const [data, setData] = useState<ProgramContentType[] | undefined>();
  const [date, setDate] = useState<string | undefined>();

  const [thoughtRecordStatus, setThoughtRecordStatus] = useState<
    ThoughtRecordStatusType | undefined
  >();

  const [isFirst, setIsFirst] = useState<boolean>(false);

  const getStatusQueryFn = useCallback(
    () => thoughtRecordApi.getStatus(thoughtRecordKey!!),
    [thoughtRecordKey]
  );

  const { refetch: refetchStatusDetail } = useQuery(
    [THOUGHTRECORD_DOMAIN, THOUGHTRECORD_STATUS_DETAIL_ENDPOINT, user?.userId],
    getStatusQueryFn,
    {
      enabled: !!user?.userId && !!thoughtRecordKey,
      onSuccess: (data) => {
        setIsFirst(data.result?.isFirst ?? false);
        setThoughtRecordStatus(data.result);
      },
    }
  );

  const {
    data: fetchedData,
    refetch,
    isFetching,
  } = useGetThoughtRecordData({
    trk: thoughtRecordKey!!,
    isFirst: isFirst,
    enabled: !!thoughtRecordKey && taskListState.length > 0 && thoughtRecordStatus !== undefined,
  });

  const refetchActivityEvent = useGetCertainEventFromApp({
    eventName: "refetch_activity",
  });

  useEffect(() => {
    if (refetchActivityEvent) {
      refetch();
    }
  }, [refetchActivityEvent]);

  useEffect(() => {
    if (fetchedData) {
      if (!_.isEqual(fetchedData.content, data)) {
        setData(fetchedData.content);
      }
    }
  }, [fetchedData]);

  useEffect(() => {
    if (data) {
      for (const item of data) {
        if (item.lines) {
          for (const line of item.lines) {
            for (const cell of line) {
              if ((cell as any).type === "date") {
                setDate((cell as any).content.value);
                return;
              }
            }
          }
        }
      }
    }
  }, [data]);

  const [moveToIndex, setMoveToIndex] = useState<number | undefined>(undefined);

  const handleUndoneSuccess = () => {
    refetchStatusDetail();
  };

  return (
    <Stack
      direction="column"
      sx={{
        height: "100%",
        width: "100%",
        backgroundColor: theme.vars.palette.Background.Level1,
      }}
    >
      <Box
        sx={{
          flex: "none",
          zIndex: 100,
          // px: { xs: "16px", sm: "24px" },
        }}
      >
        <TaskHeader
          key={thoughtRecordKey}
          taskKey={`2-2-T:${user?.userId}`}
          thoughtRecordKey={thoughtRecordKey!!}
          title={date ? `${dayjs(date).format("M월 D일")}` : ""}
          contentData={data}
          setContentData={setData}
          setMoveToIndex={setMoveToIndex}
          isDone={thoughtRecordStatus?.isDone}
          lastDoneAt={thoughtRecordStatus?.lastDoneAt}
          onUndoneSuccess={handleUndoneSuccess}
        />
      </Box>
      <Stack sx={{ flex: 1, overflow: "hidden" }} direction={"row"}>
        {thoughtRecordKey && (
          <Stack sx={{ flex: 1, overflow: "hidden" }}>
            <TaskContent
              key={thoughtRecordKey}
              taskKey={`2-2-T:${user?.userId}`}
              thoughtRecordKey={thoughtRecordKey}
              data={data}
              setData={setData}
              moveToIndex={moveToIndex}
              setMoveToIndex={setMoveToIndex}
              isDone={thoughtRecordStatus?.isDone}
              isOpen={thoughtRecordStatus?.isOpen}
              translationVersion={fetchedData?.translationVersion}
              onUndoneSuccess={handleUndoneSuccess}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
